(ns web3.log
  (:refer-clojure :exclude [println prn])
  (:require [clojure.string :as string]
            [web3.utils :as utils]
            [web3.env :as env])
  #?(:cljs (:require-macros [web3.log :as log])))

;; QUICK INTRO
;;
;; 1. use the `log` and `println` macros for logging, providing a `level`
;;    corresponding to a browser console log function, like :debug (see `js-logger`).
;;
;; 2. a sliding window of all logs is kept in memory (the `log-tail` atom).
;;
;;    for production:
;;    `tail-string` returns the last `n` logs as a string, eg. to send with error reports.
;;
;;    for development:
;;    `print-tail` prints the last `n` logs to the console using the appropriate logging
;;    function, useful for when you've hit an error and want to see the recent logs.
;;
;; `log/println` is useful because `cljs devtools` is not available in production,
;; but we'd still nice to have nice logs there for reporting purposes.


;; a string containing comma-separated names of logging levels which should be printed immediately.
;; levels not listed here will be saved to `log-tail`.

(def visible-log-levels (env/config :visible-log-levels "error warn log info"))

(defonce log-tail (atom []))

(def WINDOW-SIZE
  "Number of log items to keep in memory."
  10)

(defn log-visibly?
  "Returns true if `level` is in `visible-log-levels`"
  [level]
  (string/includes? visible-log-levels (name level)))

#?(:cljs
   (defn- conj-tail
     "Conj `x` to `tail`. If last element of `tail` is an open group, conj to the open group. Recursive."
     [tail x]
     (if (:open? (meta (peek tail)))
       (update tail (dec (count tail)) conj-tail x)
       (conj tail x))))

#?(:cljs
   (defn record-log!
     "Adds item to the log tail, preserving size of sliding window"
     [x]
     (swap! log-tail (fn [tail]
                       (utils/take-last-vec WINDOW-SIZE (conj-tail tail x))))))

#?(:cljs
   (defn close-tail-group
     "Closes deepest-open group."
     [tail]
     (if (:open? (meta (peek tail)))
       (update tail (dec (count tail)) close-tail-group)
       (with-meta tail {:open? false}))))

#?(:cljs
   (defn close-log-group []
     (swap! log-tail (fn [tail]
                       (update tail (dec (count tail)) with-meta {:open? false})))))

(def ^:private js-logger
  "References to browser console logging functions.

  Macros should refer to these by quoted symbol, while cljs code can
  reference them directly."
  {:info #?(:clj  'js/console.info
            :cljs js/console.info)
   :debug #?(:clj  'js/console.log
             :cljs js/console.log)
   :log #?(:clj  'js/console.log
           :cljs js/console.log)
   :warn #?(:clj  'js/console.warn
            :cljs js/console.warn)
   :error #?(:clj  'js/console.error
             :cljs js/console.error)
   :group/start #?(:clj  'js/console.group
                   :cljs js/console.group)
   :group/start-collapsed #?(:clj  'js/console.groupCollapsed
                             :cljs js/console.groupCollapsed)
   :group/end #?(:clj  'js/console.groupEnd
                 :cljs js/console.groupEnd)})

(defn- parse-args
  "If the first argument passed to a logging function is a keyword which
   matches a known `level`, we consider it as such, otherwise we use the
   default log level and log all the args."
  [args]
  (vec (cond->> (seq args)
                (not (contains? js-logger (first args)))
                (cons :log))))

(defprotocol IPrintLog
  "Protocol for objects that have a special way of printing for logging purposes.

   We use this to wrap arguments to `println` so that they are formatted correctly
   when printed from the tail."
  (print-log [this]))

(defn log-with
  "Returns an object which implements `print-log` and will `println-str`
   its arguments.

  Purpose is to defer the `println` operation until we actually want
  to see the value, because usually we're not printing the log, and
  println-str-ing all these debug values could slow down the UI.

  Would have done this in the macro, but the `go` macro messes up
  the quoted symbols in the `reify` form."
  [print-fn args]
  (reify IPrintLog
    (print-log [this] (apply print-fn args))))

(defn format-log-item [x]
  (if (satisfies? IPrintLog x)
    (print-log x)
    x))

(defn js-log
  "Log args with browser console logging function corresponding to `level`.

   Prefer to do this in a macro to avoid polluting the call stack."
  [level items]
  #?(:cljs
     (.apply (get js-logger level) nil (to-array (mapv format-log-item items)))
     :clj
     `(~'.apply ~(get js-logger level) nil (to-array (mapv format-log-item ~(vec items))))))

#?(:clj
   (defn- log*
     "Utility for the logging macros.

     Adds the log to the tail and, depending on visibility level,
     prints to console using appropriate js-logger function."
     [args]
     (let [[level & items :as args] (parse-args args)]
       `(do
          ~(when (log-visibly? level)
             (js-log level items))
          (record-log! ~args)
          nil))))

#?(:clj
   (do

     (defmacro log
       "Logs `args` at `level`.

       `level` can be :log, :info, :debug, :warn, or :error."
       ([arg]
        (log* [:log arg]))
       ([level & args]
        (log* (cons level args))))

     (defmacro debug
       [& args]
       (log* (cons :debug args)))

     (defmacro info
       [& args]
       (log* (cons :info args)))

     (defmacro warn
       [& args]
       (log* (cons :warn args)))

     (defmacro error
       [& args]
       (log* (cons :error args)))

     (defn- to-let-logs
       [syms]
       (->> (vec syms)
            (mapv #(identity ['_ `(info ~(name %))
                              '_ `(info ~(symbol (name %)))])) ;;strip the NS off NSed symbols
            (mapcat identity)))

     (defmacro dlet [bindings & body]
       (let [results (atom #{})
             _ (clojure.walk/postwalk
                #(do (when (symbol? %)
                       (swap! results conj %)))
                (take-nth 2 bindings))
             debug-binds (-> @results
                             to-let-logs
                             (into (reverse bindings))
                             vec)]
         `(let ~debug-binds
            ~@body)))

     (defmacro sym [symbl]
       `(do
         (info (name '~symbl))
         (info ~symbl)))))

#?(:clj
   (defmacro println
     "Logs `args` at `level`, using `print` to stringify them."
     ([arg]
      `(println :log ~arg))
     ([level & items]
      (let [[level & items] (parse-args (cons level items))]
        (log* [level `(log-with clojure.core/println-str ~(vec items))])))))

#?(:clj
   (defmacro prn
     "Logs `args` at `level`, using `print` to stringify them."
     ([arg]
      `(prn :log ~arg))
     ([level & items]
      (let [[level & items] (parse-args (cons level items))]
        (log* [level `(log-with clojure.core/prn-str ~(vec items))])))))

#?(:cljs
   (def interceptor
     "A re-frame interceptor, which logs the name of the event after it has finished."
     {:id :logger
      :before identity
      :after (fn [context]
               (log/println :debug "Finished: " (first (get-in context [:coeffects :event])))
               context)}))
;; from shodan

;; TODO
;; use timing functions that can play well with our log tail.

(defmacro time-start [& args]
  (let [[level id] (parse-args args)]
    (when (log-visibly? level)
      `(.time js/console ~id))))

(defmacro time-end [& args]
  (let [[level id] (parse-args args)]
    (when (log-visibly? level)
      `(.timeEnd js/console ~id))))

(comment

 ;; ========== Logging Examples ==========

 (log/println {:a 1 :b 2} "hi")
 (log/log {:a 1 :b 2} "hi")

 (log/println :warn {:a 1 :b 2} "hi")
 (log/log :warn {:a 1 :b 2} "hi")

 (log/println 1)
 (log/log 1)

 (log/println :error 1)
 (log/log :error 1))

;; ---------------------------------------------------------------------
;; Message grouping (browser only)

(def ^:dynamic *group-level* nil)

#?(:clj
   (defmacro group-start
     "Begin a console message group."
     [& args]
     (let [[level title] (parse-args args)]
       `(do
          ~(when (log-visibly? level)
             `(do (.group js/console ~title)
                  (some-> *group-level* (vswap! inc))))
          (record-log! ^:open? [:group/start ~title])))))

#?(:clj
   (defmacro group-start-collapsed
     "Begin a console message group in a collapsed state."
     [& args]
     (let [[level title] (parse-args args)]
       `(do
          ~(when (log-visibly? level)
             `(do (.groupCollapsed js/console ~title)
                  (some-> *group-level* (vswap! inc))))
          (record-log! ^:open? [:group/start-collapsed ~title])))))

#?(:clj
   (defmacro group-end
     "End a console message group."
     [& args]
     (let [[level] (parse-args args)]
       `(do
          ~(when (log-visibly? level)
             `(do (.groupEnd js/console)
                  (some-> *group-level* (vswap! dec))))
          (swap! log-tail close-tail-group)))))

#?(:clj
   (defmacro with-group
     "Evaluate body within a new console group with title. Returns the
     value of body."
     [& args]
     (let [[level title & body] (parse-args args)]
       `(do
          (group-start ~level ~title)
          (let [result# (do ~@body)]
            (group-end ~level)
            result#)))))

#?(:clj
   (defmacro with-group-collapsed
     "Evaluate body within a new console group (collapsed) with
     title. Returns the value of body."
     [& args]
     (let [[level title & body] (parse-args args)]
       `(do
          (group-start-collapsed ~level ~title)
          (let [result# (do ~@body)]
            (group-end ~level)
            result#)))))

;; ---------------------------------------------------------------------
;; Log tail usage

#?(:clj
   (defmacro in-group [& body]
     `(do
        (some-> *group-level* (vswap! inc))
        (let [result# (do ~@body)]
          (some-> *group-level* (vswap! dec))
          result#))))

#?(:cljs
   (defn tail-item-string
     [[level & items :as args]]
     (case level
       (:group/start
        :group/start-collapsed) (log/in-group
                                 (str \newline
                                      (first items)
                                      (-> (str \newline (string/join (mapv tail-item-string (rest items))))
                                          ;; pad with indentation
                                          (string/replace-all #"[\n\r]" (str "\n" (apply str (take @*group-level* (repeat "  "))))))
                                      "\n\n"))
       (string/join (mapv format-log-item items)))))

#?(:cljs
   (defn ^:export tail-string
     "Return last `n` items of tail as a formatted string."
     ;; TODO
     ;; use *group-level* for indentation
     ([] (tail-string WINDOW-SIZE))
     ([n]
      (binding [*group-level* (volatile! 0)]
        (->> (utils/take-vec n @log-tail)
             (mapv tail-item-string)
             (string/join))))))

#?(:cljs
   (comment
    (log/println :debug :Hello6)
    (log/group-start :info "My Group")
    (log/group-end :info)
    (clojure.core/println (tail-string))))

#?(:cljs
   (defn print-tail-item
     "Prints a single item from the tail to the console."
     [[level & items :as args]]
     (case level
       (:group/start
        :group/start-collapsed) (do
                                  (log/in-group
                                   (js-log level (take 1 items))
                                   (mapv print-tail-item (drop 2 args))
                                   (js-log :group/end [])))
       (js-log level items))))

#?(:cljs
   (defn ^:export print-tail
     "Prints last `n` items of tail to console."
     ([]
      (print-tail WINDOW-SIZE))
     ([n]
      (js/console.group "Debug Log")
      (binding [*group-level* (volatile! 0)]
        (doseq [log-item (utils/take-vec n @log-tail)]
          (print-tail-item log-item))
        (dotimes [n @*group-level*]
          (.groupEnd js/console)))
      (js/console.groupEnd))))

#?(:cljs
   (defn close-all-groups!
     "Closes all open groups."
     []
     (reset! log-tail (loop [tail @log-tail]
                        (if-not (some-> (peek tail)
                                        (meta)
                                        (:open?))
                          tail
                          (do
                            (.groupEnd js/console)
                            (recur (close-tail-group tail))))))))
#?(:cljs
   (defonce _
            ;; on page load, clear any open groups...
            ;; this can happen when "Preserve log" is enabled in devtools.
            (dotimes [_ 10]
              (.groupEnd js/console))))